// Unified Categories Configuration
// This file centralizes all category definitions for the admin panel

export interface CategoryGroup {
  name: string;
  subcategories: string[];
}

// Categories with Women's items separated into their own category
export const categories: CategoryGroup[] = [
  {
    name: "Best Sellers",
    subcategories: [
      "Best Sellers"
    ]
  },
  {
    name: "Women's",
    subcategories: [
      // Women's Tops
      "Women's T-Shirts",
      "Women's Sweaters",
      "Women's Blouses",
      "Women's Gym Wear",
      // Women's Footwear
      "High Heels",
      "Women's Slides",
      "Women's Sneakers",
      "Women's Boots",
      // Women's Bottoms
      "Women's Jeans",
      "Women's Shorts",
      "Women's Skirts",
      "Swimwear",
      // Women's Jewelry
      "Women's Bracelets",
      "Women's Necklace",
      "Women's Rings",
      "Women's Earrings",
      // Women's Outerwear
      "Women's Jackets",
      "Women's Coats",
      // Women's Accessories
      "Women's Handbags"
    ]
  },
  {
    name: "Footwear",
    subcategories: [
      "Sneakers",
      "Boots",
      "Slides",
      "Loafers",
      "Sports Shoes"
    ]
  },
  {
    name: "Tops",
    subcategories: [
      "Tees",
      "Jerseys",
      "Hoodies",
      "Zip Hoodies",
      "Crewnecks",
      "Long Sleeves",
      "Sweaters",
      "Tracksuits"
    ]
  },
  {
    name: "Bottoms",
    subcategories: [
      "Shorts",
      "Sweatpants",
      "Jeans",
      "Cargos",
      "Underwear"
    ]
  },
  {
    name: "Outerwear",
    subcategories: [
      "Jackets",
      "Varsity Jackets",
      "Windbreakers",
      "Vests",
      "Puffer Jackets"
    ]
  },
  {
    name: "Accessories",
    subcategories: [
      // Bags
      "Backpacks",
      "Duffle Bags",
      "Shoulder Bags",
      "Women's Handbags",
      // Jewelry
      "Bracelets",
      "Necklace",
      "Rings",
      "Earrings",
      // Other accessories
      "Keychains",
      "Watches",
      "Wallets",
      "Cases",
      "Glasses",
      "Belts",
      "Gloves",
      "Socks",
      "Hats & Caps"
    ]
  },
  {
    name: "Home & Lifestyle",
    subcategories: [
      "Figures",
      "Decor",
      "Lego",
      "Books",
      "Home Accessories"
    ]
  },
  {
    name: "Electronics",
    subcategories: [
      "Audio",
      "Hair Styling",
      "Gaming",
      "Gadgets"
    ]
  },
  {
    name: "Beauty",
    subcategories: [
      "Make Up",
      "Skincare",
      "Fragrances"
    ]
  }
];

// Flattened list of all categories for easy access
export const allCategories = [
  // Main categories
  ...categories.map(cat => cat.name),
  // All subcategories
  ...categories.flatMap(group => group.subcategories)
];

// Helper function to get parent category for a subcategory
export const getParentCategory = (subcategory: string): string | null => {
  for (const group of categories) {
    if (group.subcategories.includes(subcategory)) {
      return group.name;
    }
  }
  return null;
};

// Helper function to get subcategories for a parent category
export const getSubcategories = (parentCategory: string): string[] => {
  const group = categories.find(cat => cat.name === parentCategory);
  return group ? group.subcategories : [];
};

// Helper function to check if a category is a parent category
export const isParentCategory = (category: string): boolean => {
  return categories.some(cat => cat.name === category);
};

// Helper function to check if a category is a subcategory
export const isSubcategory = (category: string): boolean => {
  return categories.some(cat => cat.subcategories.includes(category));
};

// Helper function to get display name for a category
export const getCategoryDisplayName = (category: string): string => {
  // Convert kebab-case and snake_case to Title Case
  return category
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase())
    .trim();
};

// Helper function to get all categories as options for select components
export const getCategoryOptions = () => {
  return categories.map(cat => ({
    name: cat.name,
    subcategories: cat.subcategories
  }));
};

// Helper function to get flattened category options
export const getFlattenedCategoryOptions = () => {
  return [
    ...categories.map(cat => cat.name),
    ...categories.flatMap(cat => cat.subcategories)
  ];
}; 
